import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ui$, setActiveNoteId, setViewMode, setSelectedTagFilter } from '@/stores/ui$';
import { initDeepLinkListener } from '@/lib/deepLinkListener';

/**
 * Bidirectional sync between Legend-State UI state and URL.
 *
 * State → URL: when activeNoteId, viewMode, or selectedTagFilter changes, push URL.
 * URL → State: on mount and popstate (browser back), read URL and update state.
 *
 * Uses a suppressRef flag to prevent infinite loops when one side triggers the other.
 */
export function useRouteSync() {
  const navigate = useNavigate();
  const location = useLocation();
  const suppressRef = useRef(false);
  const initializedRef = useRef(false);

  // URL → State: read URL on mount and on location changes (popstate / back button)
  useEffect(() => {
    if (suppressRef.current) {
      suppressRef.current = false;
      return;
    }

    const { pathname } = location;
    applyRouteToState(pathname);
    initializedRef.current = true;
  }, [location.pathname]);

  // State → URL: observe Legend-State changes and push URL
  useEffect(() => {
    // Wait for initial URL → State to complete
    if (!initializedRef.current) return;

    const disposeActiveNote = ui$.activeNoteId.onChange(({ value: noteId }) => {
      if (suppressRef.current) {
        suppressRef.current = false;
        return;
      }
      suppressRef.current = true;

      if (noteId) {
        navigate(`/note/${noteId}`);
      } else {
        // When clearing activeNoteId, navigate based on current viewMode/tag
        const viewMode = ui$.viewMode.get();
        const tagFilter = ui$.selectedTagFilter.get();
        navigate(buildUrl(viewMode, tagFilter, null));
      }
    });

    const disposeViewMode = ui$.viewMode.onChange(({ value: mode }) => {
      if (suppressRef.current) {
        suppressRef.current = false;
        return;
      }
      // Only push URL when no note is active (note takes precedence in URL)
      if (ui$.activeNoteId.get()) return;
      suppressRef.current = true;
      navigate(buildUrl(mode, null, null));
    });

    const disposeTagFilter = ui$.selectedTagFilter.onChange(({ value: tag }) => {
      if (suppressRef.current) {
        suppressRef.current = false;
        return;
      }
      if (ui$.activeNoteId.get()) return;
      suppressRef.current = true;
      navigate(buildUrl(ui$.viewMode.get(), tag, null));
    });

    return () => {
      disposeActiveNote();
      disposeViewMode();
      disposeTagFilter();
    };
  }, [navigate]);

  // Wire up deep link listener (for native Capacitor deep links)
  useEffect(() => {
    const cleanup = initDeepLinkListener(
      (route) => {
        // Native sends routes like /note/abc, /search?q=foo, etc.
        navigate(route);
      },
      () => {
        // Sync trigger from native — no-op for routing, handled elsewhere
      }
    );
    return cleanup;
  }, [navigate]);
}

/**
 * Parse URL pathname and apply to Legend-State.
 */
function applyRouteToState(pathname) {
  // /note/:noteId
  const noteMatch = pathname.match(/^\/note\/(.+)$/);
  if (noteMatch) {
    setActiveNoteId(noteMatch[1]);
    return;
  }

  // /tag/:tagPath
  const tagMatch = pathname.match(/^\/tag\/(.+)$/);
  if (tagMatch) {
    setSelectedTagFilter(decodeURIComponent(tagMatch[1]));
    setActiveNoteId(null);
    return;
  }

  // View mode routes
  const viewRoutes = {
    '/': 'notes',
    '/favorites': 'favorites',
    '/archive': 'archive',
    '/trash': 'trash',
  };

  const viewMode = viewRoutes[pathname];
  if (viewMode) {
    setViewMode(viewMode);
    setSelectedTagFilter(null);
    setActiveNoteId(null);
    return;
  }

  // Unknown route — treat as home
  if (pathname !== '/' && !pathname.startsWith('/shared/')) {
    setViewMode('notes');
    setSelectedTagFilter(null);
    setActiveNoteId(null);
  }
}

/**
 * Build URL from state values.
 */
function buildUrl(viewMode, tagFilter, noteId) {
  if (noteId) return `/note/${noteId}`;
  if (tagFilter) return `/tag/${encodeURIComponent(tagFilter)}`;

  const viewRoutes = {
    notes: '/',
    favorites: '/favorites',
    archive: '/archive',
    trash: '/trash',
  };

  return viewRoutes[viewMode] || '/';
}
